import Moz from "@/assets/svg/Moz.svg?react";
import Readership from "@/assets/svg/Readership.svg?react";
import { Separator } from "@/components/ui/controls/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import SocialEngagementTooltip from "@/components/ui/molecules/feed-article-tooltips/SocialEngagementTooltip";
import type { SocialMetrics } from "@/components/ui/molecules/feed-article-tooltips/SocialEngagementTooltip";
import type { GetFeedArticlesPagedFromProjectionArticle } from "@/data-access/news-feeds/queries/articles-management-queries";
import type { ReactNode } from "react";
// Constants for tooltip descriptions
const DOMAIN_AUTHORITY_DESCRIPTION =
  "Domain Authority (DA) is a score developed by Moz that predicts a website's likelihood of ranking.";

const ARTICLE_READERSHIP_DESCRIPTION =
  "Estimated number of visitors to this domain";
import { formatNumber } from "./utils";

type IconProps = {
  icon: ReactNode;
  url: string;
  tooltip: string;
};

type ScoreDetailProps = Pick<
  GetFeedArticlesPagedFromProjectionArticle,
  | "publisherDomainAuthority"
  | "socialEngagement"
  | "publisherReadership"
  | "socialMetrics"
>;

const ScoreDetail: React.FC<ScoreDetailProps> = ({
  publisherDomainAuthority,
  socialEngagement,
  publisherReadership,
  socialMetrics,
}) => {
  const renderDetail = (
    iconDetail: IconProps,
    value: number | string,
    description: string,
    title: string,
  ) => (
    <TooltipProvider>
      <div className="inline-flex space-x-1 items-center text-gray-500 text-xs">
        <Tooltip>
          <TooltipTrigger className="inline-flex space-x-1 items-center text-gray-500 text-xs">
            <a href={iconDetail.url} target="_blank" rel="noreferrer">
              {iconDetail.icon}
            </a>
          </TooltipTrigger>
          <TooltipContent className="max-w-80 text-xs">
            <p>{iconDetail.tooltip}</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger className="inline-flex space-x-1 items-center text-gray-500 text-xs">
            <span>{title}:</span>
            <p className="font-bold text-gray-500">
              {typeof value === "number" ? formatNumber(value) : value}
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-80 text-xs">
            <p>{description}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );

  return (
    <div className="space-x-2 inline-flex">
      {renderDetail(
        {
          icon: <Moz className="w-6 h-6 rounded-full" />,
          url: "https://www.moz.com",
          tooltip: "Domain Authority by Moz",
        },
        publisherDomainAuthority ?? "N/A",
        DOMAIN_AUTHORITY_DESCRIPTION,
        "Domain Auth",
      )}
      <Separator orientation="vertical" className="h-4 mt-1" />
      {renderDetail(
        {
          icon: <Readership className="w-4 h-4 rounded-full" />,
          url: "https://www.similarweb.com",
          tooltip: "Readership by SimilarWeb",
        },
        publisherReadership ?? "N/A",
        ARTICLE_READERSHIP_DESCRIPTION,
        "Readership",
      )}
      <Separator orientation="vertical" className="h-4 mt-1" />
      <SocialEngagementTooltip
        score={socialEngagement}
        socialMetrics={socialMetrics as SocialMetrics | null}
        loading={false}
      />
    </div>
  );
};

export default ScoreDetail;
